import ReactDOM from 'react-dom'
import React, { FunctionComponent, useEffect } from 'react'

import {
  useRegisterManager,
  useVdEscapeStack,
} from '../../../../ng-compat/hooks'
import { closePaymentScreen } from '../hooks/usePaymentInit'
import { GiftCardModal } from '../../../gift-cards/modal/GiftCardModal'
import {
  isBigCommerceGiftCertsPaymentOption,
  isCashDiscountingPaymentOption,
  isCashPaymentOption,
  isGiftCardPaymentOption,
  isIntegratedPaymentOption,
  isLSPayEcomRefundPaymentOption,
  isLSPayPaymentOption,
  isPartialPaymentOption,
  isPaypalEcomRefundPaymentOption,
  isSwiperPaymentOption,
  PaymentOptions,
  SalePayment,
} from './types'
import { Cash } from './CashModal'
import { SwiperPayment } from './SwiperPayment'
import { LsPay } from './LsPay'
import { IntegratedPayment } from './IntegratedPayment'
import { GiftCard } from './GiftCard'
import { EcomRefund } from './EcomRefund'
import { CashDiscounting } from './CashDiscounting'
import { PartialPayment } from './PartialPayment'
import { SwiperMaintenanceModal } from './SwiperMaintenance'

type Props = {
  onClose: () => void
  onSuccess: (result: SalePayment) => Promise<void>
  paymentOptions: PaymentOptions
  tenderAmount: string
}

export const PaymentModal: FunctionComponent<Props> = ({
  tenderAmount,
  paymentOptions,
  onClose,
  onSuccess,
}) => {
  const currencySymbol = useRegisterManager().getActiveOutlet().currency_symbol
  const vdEscapeStack = useVdEscapeStack()
  /**
   * When modal is shown pressing escape key will not go back to sell screen
   * When modal is closed pressing escape key will go back to sell screen
   */
  useEffect(() => {
    vdEscapeStack.deregister(closePaymentScreen)
    return () => {
      /**
       * wrap inside a setTimeout so register is executed in the next event loop
       * this to not to go back to the sell screen when pressed ESC key while the modal opened
       */
      setTimeout(() => {
        vdEscapeStack.register(closePaymentScreen)
      }, 0)
    }
  }, [vdEscapeStack])

  switch (paymentOptions.paymentModalType) {
    case 'cash':
      if (isCashPaymentOption(paymentOptions)) {
        return ReactDOM.createPortal(
          <Cash
            paymentOptions={paymentOptions}
            onSuccess={onSuccess}
            onClose={onClose}
            currencySymbol={currencySymbol}
          />,
          document.body
        )
      }
      break
    case 'cashDiscounting':
      if (isCashDiscountingPaymentOption(paymentOptions)) {
        return ReactDOM.createPortal(
          <CashDiscounting
            paymentOptions={paymentOptions}
            onSuccess={onSuccess}
            onClose={onClose}
          />,
          document.body
        )
      }
      break
    case 'integrated':
      if (isIntegratedPaymentOption(paymentOptions)) {
        return ReactDOM.createPortal(
          <IntegratedPayment
            tenderAmount={tenderAmount}
            paymentOptions={paymentOptions}
            onSuccess={onSuccess}
            onClose={onClose}
          />,
          document.body
        )
      }
      break
    case 'giftCard':
      if (isGiftCardPaymentOption(paymentOptions)) {
        return ReactDOM.createPortal(
          <GiftCard
            paymentOptions={paymentOptions}
            onSuccess={onSuccess}
            onClose={onClose}
          />,
          document.body
        )
      }
      break
    case 'lsPay':
      if (isLSPayPaymentOption(paymentOptions)) {
        return ReactDOM.createPortal(
          <LsPay
            paymentOptions={paymentOptions}
            onClose={onClose}
            onSuccess={onSuccess}
          />,
          document.body
        )
      }
      break

    case 'lsPayEcomRefund':
      if (isLSPayEcomRefundPaymentOption(paymentOptions)) {
        return ReactDOM.createPortal(
          <EcomRefund
            paymentOption={paymentOptions}
            onClose={onClose}
            onSuccess={onSuccess}
          />,
          document.body
        )
      }
      break
    case 'paypalEcomRefund':
      if (isPaypalEcomRefundPaymentOption(paymentOptions)) {
        return ReactDOM.createPortal(
          <EcomRefund
            paymentOption={paymentOptions}
            onClose={onClose}
            onSuccess={onSuccess}
          />,
          document.body
        )
      }
      break
    case 'lspayRecovery':
      return null
    case 'partialPayment':
      if (isPartialPaymentOption(paymentOptions)) {
        return ReactDOM.createPortal(
          <PartialPayment
            paymentOptions={paymentOptions}
            onClose={onClose}
            onSuccess={onSuccess}
          />,
          document.body
        )
      }
      break
    case 'swiper':
      if (isSwiperPaymentOption(paymentOptions)) {
        return ReactDOM.createPortal(
          <SwiperPayment
            paymentOptions={paymentOptions}
            onClose={onClose}
            onSuccess={onSuccess}
          />,
          document.body
        )
      }
      break
    case 'bigcommerceGiftCerts':
      if (isBigCommerceGiftCertsPaymentOption(paymentOptions)) {
        const { paymentType, amount } = paymentOptions

        return (
          <GiftCardModal
            giftCardType="BIGCOMMERCE"
            flow="REDEEMING"
            paymentType={paymentType}
            amount={amount}
            onClose={onClose}
            onSuccess={onSuccess}
          />
        )
      }
      break
    case 'swiperMaintenance':
      return ReactDOM.createPortal(
        <SwiperMaintenanceModal onClose={onClose} />,
        document.body
      )

    default: {
      // Some default payment type should not have modal
      return null
    }
  }
}
